// frontend/src/components/client/mobile/MobileHangars.jsx
import React, { useState, useEffect } from 'react';
import { Warehouse, Calendar, MapPin, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { getMyHangars } from '../../../services/clientApi';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US') : '—';

const ProgressBar = ({ pct }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
      <span style={{ fontSize: 10, color: '#64748b' }}>Lease progress</span>
      <span style={{ fontSize: 10, color: '#64748b' }}>{pct}%</span>
    </div>
    <div style={{ height: 6, background: '#0f172a', borderRadius: 3, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: '#22c55e', borderRadius: 3, transition: 'width 0.4s ease' }} />
    </div>
  </div>
);

const MobileHangars = () => {
  const [hangars, setHangars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    getMyHangars()
      .then(r => setHangars(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
      <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );

  if (hangars.length === 0) return (
    <div style={{ margin: '0 16px', textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
      <Warehouse size={40} color="#334155" style={{ marginBottom: 12 }} />
      <p style={{ color: '#475569' }}>No active hangar leases</p>
    </div>
  );

  return (
    <div style={{ padding: '0 16px 100px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {hangars.map((h) => {
        const expanded = expandedId === h.id;
        return (
          <div key={h.id} style={{ background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155', overflow: 'hidden' }}>
            {/* Header row — always visible */}
            <button
              onClick={() => setExpandedId(expanded ? null : h.id)}
              style={{ width: '100%', background: 'none', border: 'none', padding: '16px', cursor: 'pointer', textAlign: 'left', WebkitTapHighlightColor: 'transparent' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(234,179,8,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Warehouse size={20} color="#eab308" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>Hangar #{h.hangar_number}</span>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600 }}>Active</span>
                  </div>
                  <p style={{ fontSize: 12, color: '#64748b', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.hangar_name}</p>
                  {h.location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={10} color="#475569" />
                      <span style={{ fontSize: 11, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.location}</span>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>/ month</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{fmt(h.monthly_rent)}</div>
                  <div style={{ marginTop: 6 }}>
                    {expanded ? <ChevronUp size={16} color="#475569" /> : <ChevronDown size={16} color="#475569" />}
                  </div>
                </div>
              </div>
            </button>

            {/* Expanded detail */}
            {expanded && (
              <div style={{ borderTop: '0.5px solid #334155', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Calendar size={12} color="#475569" />
                    <span style={{ fontSize: 11, color: '#64748b' }}>Lease period</span>
                  </div>
                  <p style={{ fontSize: 14, color: '#e2e8f0' }}>{fmtDate(h.start_date)} – {fmtDate(h.end_date)}</p>
                  {h.days_remaining > 0 && (
                    <p style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{h.days_remaining} days remaining</p>
                  )}
                </div>
                <ProgressBar pct={h.progress_percentage || 0} />
                {h.assigned_aircraft_tail && (
                  <div style={{ paddingTop: 10, borderTop: '0.5px solid #334155' }}>
                    <span style={{ fontSize: 11, color: '#64748b' }}>Assigned aircraft</span>
                    <p style={{ fontSize: 14, color: '#e2e8f0', marginTop: 2 }}>{h.assigned_aircraft_tail}</p>
                  </div>
                )}
                <button style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#3b82f6', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '4px 0' }}>
                  <FileText size={15} /> View contract
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MobileHangars;